import { useEffect, useRef, useCallback, useState } from 'react'
import { useTheme } from 'styled-components'
import { Icon } from 'components'
import { WHEEL_SIZE, RADIUS, hexToHsv, hsvToHex, drawWheel, drawSelector } from './components/colorWheel'
import {
  Wrapper,
  WheelCanvas,
  SliderLabel,
  BrightnessTrack,
  BrightnessThumb,
  Footer,
  Swatch,
  HexInput,
  IconButton,
} from './components/ColorPickerStyles'

// ── Component ─────────────────────────────────────────────────────────────────

export const ColorPicker = ({
  color = '#2a2a2a',
  onChange,
  isPicking = false,
  onStartPick,
  isErasing = false,
  onToggleErase,
}) => {
  const { colors } = useTheme()
  const canvasRef = useRef()
  const draggingRef = useRef(false)
  const [hsv, setHsv] = useState(() => hexToHsv(color))
  const [hexInput, setHexInput] = useState(color)

  useEffect(() => {
    const next = hexToHsv(color)
    setHsv(next)
    setHexInput(color)
  }, [color])

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    drawWheel(ctx, hsv.v)
    drawSelector(ctx, hsv.h, hsv.s)
  }, [hsv.h, hsv.s, hsv.v])

  const pickFromWheel = useCallback((e) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX - rect.left) * (WHEEL_SIZE / rect.width)
    const y = (e.clientY - rect.top) * (WHEEL_SIZE / rect.height)
    const cx = WHEEL_SIZE / 2, cy = WHEEL_SIZE / 2
    const dx = x - cx, dy = y - cy
    const hue = ((Math.atan2(dy, dx) * 180 / Math.PI) + 360) % 360
    const sat = Math.min(Math.sqrt(dx * dx + dy * dy) / RADIUS, 1)
    const next = { h: hue, s: sat, v: hsv.v }
    setHsv(next)
    const hex = hsvToHex(next)
    setHexInput(hex)
    onChange(hex)
  }, [hsv.v, onChange])

  useEffect(() => {
    const onMove = (e) => { if (draggingRef.current) pickFromWheel(e) }
    const onUp = () => { draggingRef.current = false }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [pickFromWheel])

  const onBrightnessClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const v = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const next = { ...hsv, v }
    setHsv(next)
    const hex = hsvToHex(next)
    setHexInput(hex)
    onChange(hex)
  }, [hsv, onChange])

  const onHexInput = (e) => {
    const val = e.target.value
    setHexInput(val)
    if (/^#[0-9a-f]{6}$/i.test(val)) {
      setHsv(hexToHsv(val))
      onChange(val)
    }
  }

  const darkColor = hsvToHex({ h: hsv.h, s: hsv.s, v: 0 })
  const fullColor = hsvToHex({ h: hsv.h, s: hsv.s, v: 1 })

  return (
    <Wrapper>
      <WheelCanvas
        ref={canvasRef}
        width={WHEEL_SIZE}
        height={WHEEL_SIZE}
        onMouseDown={(e) => { draggingRef.current = true; pickFromWheel(e) }}
      />

      <div style={{ width: '100%', minWidth: 0 }}>
        <SliderLabel>Brightness</SliderLabel>
        <BrightnessTrack from={darkColor} to={fullColor} onClick={onBrightnessClick}>
          <BrightnessThumb left={hsv.v * 100} />
        </BrightnessTrack>
      </div>

      <Footer>
        <Swatch color={color} />
        <HexInput value={hexInput} onChange={onHexInput} spellCheck={false} />
        {onStartPick && (
          <IconButton
            type="button"
            $active={isPicking}
            onClick={onStartPick}
            title={isPicking ? 'Click a tile to sample its color' : 'Sample color from tile'}
          >
            <Icon icon="eyedropper" width={14} height={14} fill={isPicking ? colors('highlight') : colors('highlight', 0.55)} />
          </IconButton>
        )}
        {onToggleErase && (
          <IconButton
            type="button"
            $active={isErasing}
            onClick={onToggleErase}
            title={isErasing ? 'Stop erasing' : 'Erase unsaved pixels'}
          >
            <Icon icon="eraser" width={14} height={14} fill={isErasing ? colors('highlight') : colors('highlight', 0.55)} />
          </IconButton>
        )}
      </Footer>
    </Wrapper>
  )
}
